import { useState, useRef, useEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  Menu, X, LogOut, BarChart3, Users, Calendar, FileText, MapPin, Shield,
  CalendarCheck, User, ChevronDown, CalendarDays, Sparkles, Search,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { cn } from '../lib/utils'
import { authAPI } from '../services/api'
import { useToast } from '../components/Toast'

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  )
  const [collapsed, setCollapsed] = useState(false)
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false)
  const headerDropdownRef = useRef<HTMLDivElement>(null)
  const { user, clearAuth } = useAuthStore()
  const { success } = useToast()
  const location = useLocation()

  // Grouped nav — easier to scan and mirrors real-world admin layouts
  const navGroups = [
    {
      label: 'Overview',
      items: [
        { path: '/admin/dashboard', label: 'Dashboard', icon: BarChart3 },
      ],
    },
    {
      label: 'People',
      items: [
        { path: '/admin/employees', label: 'Employees', icon: Users },
        { path: '/admin/attendance', label: 'Attendance', icon: Calendar },
        { path: '/admin/leave', label: 'Leave Requests', icon: CalendarCheck },
      ],
    },
    {
      label: 'Schedule',
      items: [
        { path: '/admin/holidays', label: 'Holidays', icon: CalendarDays },
        { path: '/admin/week-offs', label: 'Week Offs', icon: Calendar },
      ],
    },
    {
      label: 'Records',
      items: [
        { path: '/admin/reports', label: 'Reports', icon: FileText },
        { path: '/admin/centers', label: 'Centers', icon: MapPin },
        { path: '/admin/audit', label: 'Audit Log', icon: Shield },
      ],
    },
  ]

  // Close header dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerDropdownRef.current && !headerDropdownRef.current.contains(e.target as Node)) {
        setIsHeaderDropdownOpen(false)
      }
    }
    if (isHeaderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isHeaderDropdownOpen])

  // Close header dropdown on route change
  useEffect(() => {
    setIsHeaderDropdownOpen(false)
  }, [location.pathname])

  const handleLogout = async () => {
    try {
      await authAPI.logout()
    } catch {
      // ignore — clear local session regardless
    } finally {
      success('Logged out successfully.')
      clearAuth()
      setTimeout(() => {
        window.location.href = '/login'
      }, 500)
    }
  }

  const isViewer = user?.role === 'viewer'

  return (
    <div className="flex h-screen bg-[#FAF6EE]">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 bg-[#0F172A] text-white transition-all duration-300 md:relative md:translate-x-0 overflow-hidden flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'md:w-20 w-72' : 'w-72'
        )}
      >
        {/* Ambient gradient orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-[#F0821F]/20 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[#A31621]/20 blur-3xl" />
        </div>

        <div className="relative flex flex-col h-full">
          {/* Logo */}
          <div className={cn('border-b border-white/[0.06] flex-shrink-0', collapsed ? 'p-4' : 'p-6')}>
            <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-black/30 flex-shrink-0">
                <img
                  src="/logo.png"
                  alt="IRCS Haryana"
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                    const fallback = document.getElementById('admin-logo-fallback')
                    if (fallback) fallback.style.display = 'block'
                  }}
                />
                <Shield id="admin-logo-fallback" className="w-5 h-5 text-[#C24A0A]" style={{ display: 'none' }} />
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <h1 className="font-bold text-lg leading-tight">IRCS Haryana</h1>
                  <p className="text-xs text-white/50 leading-tight">Admin Console</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation (grouped) */}
          <nav className="flex-1 px-3 py-5 overflow-y-auto overflow-x-hidden">
            {navGroups.map((group, gi) => (
              <div key={group.label} className={gi > 0 ? 'mt-5' : ''}>
                {!collapsed && (
                  <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/30">
                    {group.label}
                  </p>
                )}
                {collapsed && gi > 0 && (
                  <div className="mx-auto mb-3 w-6 h-px bg-white/10" />
                )}
                <div className="space-y-1">
                  {group.items.map(({ path, label, icon: Icon }) => {
                    const active = location.pathname === path
                    return (
                      <Link
                        key={path}
                        to={path}
                        title={collapsed ? label : undefined}
                        onClick={() => {
                          if (window.innerWidth < 768) setSidebarOpen(false)
                        }}
                        className={cn(
                          'group relative flex items-center rounded-xl transition-all duration-200 text-sm',
                          collapsed ? 'justify-center p-2.5' : 'gap-3 px-3.5 py-2.5',
                          active
                            ? 'bg-gradient-to-r from-[#F0821F] to-[#E4670F] text-white shadow-lg shadow-[#E4670F]/30 font-semibold'
                            : 'text-white/70 hover:text-white hover:bg-white/[0.06] font-medium'
                        )}
                      >
                        {active && !collapsed && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-white/80" />
                        )}
                        <Icon
                          className={cn(
                            'w-[18px] h-[18px] flex-shrink-0 transition-transform',
                            !active && 'group-hover:scale-110'
                          )}
                        />
                        {!collapsed && <span className="truncate">{label}</span>}

                        {/* Collapsed tooltip */}
                        {collapsed && (
                          <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg bg-[#1E293B] text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all shadow-xl ring-1 ring-white/10 z-50">
                            {label}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User section — simple, no dropdown */}
          <div className={cn('border-t border-white/[0.06] flex-shrink-0', collapsed ? 'p-3' : 'p-4')}>
            {collapsed ? (
              <div className="flex flex-col items-center gap-2">
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    title={user.full_name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[#F0821F]/40"
                  />
                ) : (
                  <div
                    title={user.full_name}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white/10"
                  >
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                  className="p-2 text-white/50 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-white/[0.05] border border-white/[0.06] rounded-xl">
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-[#F0821F]/40"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ring-2 ring-white/10">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-[11px] text-white/40 uppercase tracking-wide">
                    {isViewer ? 'Viewer' : 'Admin'}
                  </p>
                  <p className="font-semibold text-white truncate text-sm leading-tight">{user?.full_name}</p>
                </div>
                <button
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                  className="p-2 text-white/50 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white/80 backdrop-blur-md border-b border-neutral-200/70 px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4 sticky top-0 z-30">
          {/* Mobile hamburger */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="w-5 h-5 text-neutral-700" /> : <Menu className="w-5 h-5 text-neutral-700" />}
          </button>

          {/* Desktop collapse toggle — in same slot as hamburger */}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="hidden md:inline-flex p-2 hover:bg-neutral-100 rounded-lg transition-colors text-neutral-600 hover:text-neutral-900"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-neutral-900 truncate">
                {isViewer ? 'Admin Portal — View Only' : 'Admin Portal'}
              </h2>
              {isViewer && (
                <span className="hidden sm:inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  <Shield className="w-3 h-3" />
                  Read-only
                </span>
              )}
            </div>
          </div>

          {/* Right side quick actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* <div className="hidden lg:flex items-center gap-2 bg-neutral-100 rounded-lg px-3 py-1.5 text-sm text-neutral-400 w-56">
              <Search className="w-4 h-4" />
              <span className="text-xs">Search...</span>
            </div> */}
            <div className="hidden md:inline-flex items-center gap-1.5 bg-gradient-to-r from-[#F0821F]/10 to-[#E4670F]/10 border border-[#E4670F]/20 rounded-full px-3 py-1 text-[11px] font-semibold text-[#C24A0A]">
              <Sparkles className="w-3 h-3" />
              {isViewer ? 'Viewer mode' : 'Active'}
            </div>

            {/* ── Profile Dropdown (header) ── */}
            <div className="relative" ref={headerDropdownRef}>
              <button
                onClick={() => setIsHeaderDropdownOpen((v) => !v)}
                className={cn(
                  'flex items-center gap-2 p-1 rounded-full transition-all duration-200',
                  isHeaderDropdownOpen
                    ? 'ring-2 ring-[#E4670F]/40 bg-[#E4670F]/5'
                    : 'hover:ring-2 hover:ring-neutral-200'
                )}
                aria-label="Open profile menu"
                aria-expanded={isHeaderDropdownOpen}
              >
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-[#F0821F]/30"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-sm font-bold ring-2 ring-white">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown
                  className={cn(
                    'hidden sm:block w-3.5 h-3.5 text-neutral-400 transition-transform duration-200',
                    isHeaderDropdownOpen && 'rotate-180'
                  )}
                />
              </button>

              {isHeaderDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl shadow-black/10 ring-1 ring-neutral-200/70 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* User info header */}
                  <div className="px-4 py-3.5 border-b border-neutral-100 bg-gradient-to-br from-neutral-50 to-white">
                    <div className="flex items-center gap-3">
                      {user?.photo ? (
                        <img
                          src={user.photo}
                          alt={user.full_name}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-[#F0821F]/30"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white font-bold ring-2 ring-white">
                          {user?.full_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-neutral-900 truncate">
                          {user?.full_name}
                        </p>
                        <p className="text-[11px] text-neutral-500 truncate">
                          {user?.email || (isViewer ? 'Viewer' : 'Admin')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2.5">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ring-1',
                          isViewer
                            ? 'bg-blue-50 text-blue-700 ring-blue-200'
                            : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            isViewer ? 'bg-blue-500' : 'bg-emerald-500'
                          )}
                        />
                        {isViewer ? 'Viewer (read-only)' : 'Admin'}
                      </span>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="p-1.5">
                    <Link
                      to="/admin/profile"
                      onClick={() => setIsHeaderDropdownOpen(false)}
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-[#E4670F]/[0.06] hover:text-[#C24A0A] transition-colors text-sm font-medium"
                    >
                      <div className="w-8 h-8 rounded-lg bg-neutral-100 group-hover:bg-[#E4670F]/10 flex items-center justify-center transition-colors">
                        <User className="w-4 h-4" />
                      </div>
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        setIsHeaderDropdownOpen(false)
                        handleLogout()
                      }}
                      className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors text-sm font-medium mt-0.5"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                        <LogOut className="w-4 h-4" />
                      </div>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}